import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    AddSubSubProduct,
    AddUpdateRemoveProductAttributes,
    AddUpdateRemoveSubProductAttributes,
    DeletePricingOption,
    LoadProductDetails,
    LoadProductDetailsFailure,
    LoadProductDetailsSuccess,
    LoadProductsByIdsSuccess,
    ProductActionFailure,
    ReloadProduct,
    ReloadProductFailure,
    ReloadProductSuccess,
    RemoveProductAttachment,
    RemoveSubProduct,
    RemoveSubProductRestriction,
    UpdatePricingOptionsOrderSuccess,
    UpsertPricingOption,
    UpsertProduct,
    UpsertProductAttachment,
    UpsertProductFailure,
    UpsertProductSuccess,
    UpsertSubProduct,
    UpsertSubProductRestriction
} from '../actions/product.actions';
import { AppState } from '../store';
import { ProductEntity } from '../entity-models';
import { buildProductEntities } from '../entity-builder-utils';

/** Holds the full product graph (attributes, sub products, pricing options), never the slim list. */
export interface ProductState extends EntityState<ProductEntity> {
    /** The id the running request was started for; it makes a superseded request recognizable. */
    requestedProductId: string;
    loading: boolean;
    error: any;
    actionLoading: boolean;
    lastCreatedProductId: string;
}

export const productAdapter: EntityAdapter<ProductEntity> = createEntityAdapter<ProductEntity>({
    selectId: (product) => product.productId,
    sortComparer: (a, b) => moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime()
});

export const initialState: ProductState = productAdapter.getInitialState({
    requestedProductId: undefined,
    loading: false,
    error: undefined,
    actionLoading: false,
    lastCreatedProductId: undefined
});

export const productReducer = createReducer(
    initialState,
    on(LoadProductDetails, (state, { productId }) => ({ ...state, requestedProductId: productId, loading: true, error: undefined })),
    on(LoadProductDetailsSuccess, (state, { products, language }) =>
        productAdapter.upsertMany(buildProductEntities(products, language || 'de'), { ...state, loading: false })
    ),
    on(LoadProductDetailsFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(LoadProductsByIdsSuccess, (state, { products }) => productAdapter.upsertMany(buildProductEntities(products || []), state)),

    on(
        UpsertProduct,
        UpsertSubProduct,
        RemoveSubProduct,
        UpsertPricingOption,
        DeletePricingOption,
        AddSubSubProduct,
        AddUpdateRemoveProductAttributes,
        AddUpdateRemoveSubProductAttributes,
        UpsertSubProductRestriction,
        RemoveSubProductRestriction,
        UpsertProductAttachment,
        RemoveProductAttachment,
        (state) => ({ ...state, actionLoading: true })
    ),

    on(UpsertProductSuccess, (state, { productId }) => ({ ...state, lastCreatedProductId: productId })),
    on(UpsertProductFailure, (state) => ({ ...state, actionLoading: false })),

    on(UpdatePricingOptionsOrderSuccess, (state, { pricingOptions, productId }) =>
        productAdapter.mapOne({ id: productId, map: (entity) => ({ ...entity, productPricingOptions: pricingOptions }) }, state)
    ),

    on(ReloadProduct, (state) => ({ ...state, actionLoading: true })),
    on(ReloadProductSuccess, (state, { product }) => ({
        ...productAdapter.upsertMany(buildProductEntities([product]), state),
        actionLoading: false
    })),
    // A failed mutation must not land in `error` — that one says the visited product could not be loaded.
    on(ReloadProductFailure, ProductActionFailure, (state) => ({ ...state, actionLoading: false }))
);

const { selectAll: selectAllFullProducts } = productAdapter.getSelectors();

export const productState = (state: AppState) => state.productState;

export const allFullProducts = createSelector(productState, (state) => selectAllFullProducts(state));
export const fullProductsLoading = createSelector(productState, (state) => state.loading);
export const requestedFullProductId = createSelector(productState, (state) => state.requestedProductId);

export const productActionLoading = createSelector(productState, (state) => state.actionLoading);
export const lastCreatedProductId = createSelector(productState, (state) => state.lastCreatedProductId);
